/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 16/05/2025 16:55
 *
 */

import { inject, Injectable } from '@angular/core';
import { AuthorizationService, RestService } from '@infodata-group/inf-commons';
import { Observable } from 'rxjs';
import { FactoryVigiMenu } from '../factory/factory-vigi-menu';
import { CanDeactivateComponent } from '../model/can-deactivate-component';
import { VigiMenuItem } from '../model/vigi-menu-item';
import { Tools } from '../modules/shared/engine/tools';
import { VigiguardService } from './vigiguard.service';

@Injectable({
  providedIn: 'root',
})
export class VigiMenuService {
  private readonly restService = inject(RestService);
  private readonly vigiguardService = inject(VigiguardService);
  private readonly authorizationService = inject(AuthorizationService);

  public vigiMenuItems: VigiMenuItem[] = [];
  public additionalUrl: string[] = [
    '/redirect',
    '/dpworld/select-lodge',
    '/alarms',
  ];
  public isLoaded = false;
  public currentComponent: CanDeactivateComponent;

  constructor() {
    this.vigiguardService.loggingOut$().subscribe(() => {
      this.vigiMenuItems = [];
      this.isLoaded = false;
    });
  }

  public loadMenu$(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (
        !this.authorizationService.isAuthenticated ||
        !this.authorizationService.userContext ||
        !this.authorizationService.userContext.group ||
        this.authorizationService.userContext.group.length === 0
      ) {
        observer.next(false);
        this.vigiguardService.logout();
        return;
      }
      const sub = this.restService
        .getById(new FactoryVigiMenu(), 'MNU', {
          showLoading: false,
        })
        .subscribe({
          next: (data) => {
            this.vigiMenuItems = [];
            for (const item of data.items) {
              if (this.checkAuthorization(item)) {
                this.vigiMenuItems.push(item);
              }
            }
            this.isLoaded = true;
            sub.unsubscribe();
            observer.next(true);
          },
          error: (e) => {
            Tools.errorObserver(e);
            observer.next(false);
            sub.unsubscribe();
            this.vigiguardService.logout();
          },
        });
    });
  }

  public findByUrl(url: string): VigiMenuItem {
    url = this.removeGetParamsFromUrl(url);
    // Particular case :: incident & vaststelling
    if (url.indexOf('incident') > -1 || url.indexOf('vaststelling')) {
      let array = url.split('/');
      if (array.length === 5) {
        array = array.slice(0, -2);
        url = array.join('/');
      }
    }
    const result = this.findMultipleByUrl(url);
    switch (result.length) {
      case 1:
        return result[0];
      case 2:
        return result[1];
      case 3:
        return result[2];
      default:
        return null;
    }
  }

  public findMultipleByUrl(url: string): VigiMenuItem[] {
    url = url[0] !== '/' ? `/${url}` : url;
    url = this.removeGetParamsFromUrl(url);
    const result = this.vigiMenuItems
      .map((x) => x.isMatching(url, 'customRoute'))
      .filter((x) => x.length > 0);
    if (result.length !== 1) {
      return [];
    }
    return result[0];
  }

  public getFirstItem(): VigiMenuItem[] {
    for (const item of this.vigiMenuItems) {
      if (item.customRoute) {
        return [item];
      }
      if (item.hasSubMenus()) {
        for (const subItem of item.submenus) {
          if (subItem.customRoute) {
            return [item, subItem];
          }
        }
      }
    }
    return [];
  }

  public isAllowed(url: string) {
    url = this.removeGetParamsFromUrl(url);
    if (this.findByUrl(url) !== undefined) {
      return true;
    }
    if (this.additionalUrl.find((x) => url.includes(x))) {
      return true;
    }
    return false;
  }

  private checkAuthorization(item: VigiMenuItem): boolean {
    let subMenuRead = false;
    if (item.hasSubMenus()) {
      for (const subItem of item.submenus) {
        const hasRight = this.checkAuthorization(subItem);
        if (!hasRight) {
          subItem.disabled = true;
          continue;
        }
        subMenuRead = true;
      }
    }

    if (subMenuRead) {
      return true;
    }

    if (!item.CRUD) {
      return true;
    }

    // If no group configure on read => ignore
    if (!item.CRUD.read) {
      return true;
    }

    // Check read authorization
    let hasRightToRead = false;
    for (const group of this.authorizationService.userContext.group) {
      if (item.CRUD.read.includes(group)) {
        hasRightToRead = true;
      }
    }
    return hasRightToRead;
  }

  public getToolbarIcon(url: string): string {
    try {
      const items = this.findMultipleByUrl(url);
      return items.at(0).icon;
    } catch (e) {
      console.warn(`Impossible to find icon for url "${url}"`);
      return '';
    }
  }

  public removeGetParamsFromUrl(url: string): string {
    const splitGet = url.split('?');
    if (splitGet.length > 1) {
      return splitGet.at(0);
    }
    return url;
  }
}
